/**
 * src/stores/voice.ts
 * ---------------------
 * Zustand store for voice pipeline state.
 *
 * Holds:
 *   - state (VoiceState) — drives the orb + state label
 *   - amplitudes — live mic FFT bins (~30fps) for the waveform
 *   - turns — conversation transcript (You / Helix), updated live while the
 *     assistant streams its reply
 *   - isMuted
 */
import { create } from 'zustand'
import type { VoiceState, VoiceTurn } from '@/types/voice'

interface VoiceStore {
  state: VoiceState
  amplitudes: number[]
  turns: VoiceTurn[]
  isMuted: boolean
  voiceId: string

  setState: (state: VoiceState) => void
  setAmplitudes: (amps: number[]) => void
  /** Add or live-update a turn. A partial assistant turn updates in place. */
  upsertTurn: (role: 'user' | 'assistant', text: string, partial?: boolean) => void
  setMuted: (v: boolean) => void
  setVoiceId: (v: string) => void
  resetSession: () => void
}

export const useVoiceStore = create<VoiceStore>((set) => ({
  state: 'idle',
  amplitudes: new Array(40).fill(0),
  turns: [],
  isMuted: false,
  voiceId: localStorage.getItem('helix-voice') || 'alloy',

  setState: (state) => set({ state }),

  setAmplitudes: (amplitudes) => set({ amplitudes }),

  upsertTurn: (role, text, partial = false) =>
    set((s) => {
      const turns = [...s.turns]
      const last = turns[turns.length - 1]
      if (last && last.role === role && last.partial) {
        turns[turns.length - 1] = { role, text, partial }
      } else {
        turns.push({ role, text, partial })
      }
      return { turns }
    }),

  setMuted: (isMuted) => set({ isMuted }),

  setVoiceId: (voiceId) => {
    localStorage.setItem('helix-voice', voiceId)
    set({ voiceId })
  },

  resetSession: () =>
    set({
      state: 'idle',
      amplitudes: new Array(40).fill(0),
      turns: [],
      isMuted: false,
    }),
}))
